import type { IProductDomain } from '@/Interfaces/ProductDomains/ProductDomain.interface';
import ExportIcon from '@/components/Icons/ExportIcon';
import { downloadTableCsv } from '@/helpers/downloadCSV';
import { slugifyForFilename } from '@/helpers/file';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import classNames from 'classnames';
import { useCallback } from 'react';

interface IProps {
  domains: IProductDomain[];
  productName: string;
  isLoading: boolean;
  error: Error | null;
  className?: string;
}

const ProductDomainExport = ({ domains, productName, isLoading, error, className }: IProps) => {
  const handleExport = useCallback(() => {
    const rows = domains.map(({ name }) => ({ Domain: name }));
    const filename = `${slugifyForFilename(productName) || 'product'}-domains`;
    downloadTableCsv(rows, filename);
  }, [domains, productName]);

  const hasDomains = domains.length > 0;
  const helperText = error
    ? 'Could not load domains for this product'
    : !isLoading && !hasDomains
      ? 'No domains available for this product'
      : null;

  return (
    <Box className={classNames('flex flex-col items-center gap-1 py-3 bg-[#E8E8E8]/40', className)}>
      {isLoading ? (
        <CircularProgress size={20} />
      ) : (
        <>
          <Button variant='contained' startIcon={<ExportIcon />} onClick={handleExport} disabled={!hasDomains}>
            Export List
          </Button>
          {helperText && (
            <Typography variant='caption' color='text.secondary'>
              {helperText}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default ProductDomainExport;
